import {
  DataSource,
  DataImport,
  DataSourceType,
  Column,
  GoogleAnalyticsSchema,
  TableauWorksheetZone,
  RunDataImportRequest,
  AnalyticsEvent,
  ExcelFileLocationType,
} from "@coeff/api";
import {
  MSSQL_WIRE_DATA_SOURCE_TYPES,
  MYSQL_WIRE_DATA_SOURCE_TYPES,
  POSTGRES_WIRE_DATA_SOURCE_TYPES,
} from "@coeff/utils";

import { API_KEYS } from "./api-keys";
import { BuildFlags } from "./build-flags";
import { API_ROUTES, BASE_URL } from "./config";

export { API_ROUTES, BASE_URL };

export * from "./filters";
export * from "./getSyncMode";
export * from "./utils";

export const TRIX_DB_TAB_NAME = "Coefficient Internal";

export const COEFF_IMAGE_STORE_BASE_URL = "https://coeff-image-store.s3-us-west-2.amazonaws.com";

// BE apis have a timeout after 6mins. Only allow data import run calls to run for 30
// seconds before returning to avoid hitting the timeout. Keeping the value 30 seconds,
// much lower than 6 mins otherwise some requests can still take more than 6 mins. This
// happens sometime when running multiple imports at once. The requests can wait in NGINX
// queue for other requests to finish effectively increasing the total wait time.
// See: https://app.clickup.com/t/8685349r3
//
// It is now changed to 0 and poll is done every few seconds, this avoids busy wait and
// puts less load on the server. See: https://github.com/coeff/coeff/pull/11310
export const DATA_IMPORT_REQUEST_MAX_WAIT_SECS = 0;

export const ASYNC_REFRESH_ENABLED_NON_SQL_DATA_SOURCE_TYPES: Array<DataSourceType> = [
  "looker",
  "outreach",
  "gmail",
  "smartlead",
];

export const ASYNC_REFRESH_ENABLED_SQL_DATA_SOURCE_TYPES: Array<DataSourceType> = [
  ...Array.from(MYSQL_WIRE_DATA_SOURCE_TYPES),
  ...Array.from(POSTGRES_WIRE_DATA_SOURCE_TYPES),
  ...Array.from(MSSQL_WIRE_DATA_SOURCE_TYPES),
  "redshift",
  "snowflake",
  "bigquery",
];

export const APPEND_IMPORT_ENABLED_DATA_SOURCE_TYPES: Array<DataSourceType> = ["chargebee"];

export const APPEND_IMPORT_ENABLED_PLATFORMS = ["google_sheets"];

// export type TDataImportStatus = "pre-import" | "import-error" | "existing-import-fetching" | "existing-import"
export type TDataImportStatus = string;

export type RunDataImportOptionsEventFrom =
  | "template"
  | "import_screen"
  | "home_screen"
  | "onsheet_btn"
  | "onsheet"
  | "onsheet_refresh_btn";

export type OpenSidePanelParams = {
  initTimings?: {
    gasFunctionStart: number;
    gasFunctionName: string;
  };
  redirectOnOpen?: boolean;
  redirectPath?: string;
  redirectDataImportId?: string;
  redirectDataImportShowUsageWarning?: boolean;
  redirectDataImportTabId?: number;
  runDataImportTabId?: number;
  redirectWritebackTabId?: number;
  showCloneConfirmOnOpen?: string;
  showChatOnOpen?: boolean;
  runAllDataImportsOnOpen?: boolean;
  onsheetDataImportIdsUnableToRefresh?: string[];
  openSidepanelTriggeredBy?: string;
};

export type RunDataImportOptions = {
  analytics_props: Omit<AnalyticsEvent, "email" | "domain">;
  isScheduledRun?: boolean;
  isTriggeredByParameterizedFilter?: boolean;
  eventFrom?: RunDataImportOptionsEventFrom;
} & Pick<RunDataImportRequest, "import_start_option" | "background_after_secs">;

export interface DataImportFile {
  id: string;
  name: string;
  sizeBytes?: number;
  url: string;
}

export type DataImportFiles = DataImportFile[];
export interface DataImportStatus {
  status?: TDataImportStatus;
}

// A Tab in a GSheets file
export interface Tab {
  tabId: number;
  tabName: string;
  columns: Column[];
}

export interface TabInfo {
  name: string;
  sheetA1Notation: string;
  isActive: boolean;
  sheetId: number;
  // When true, this tab is hidden in the spreadsheet UI and should generally be
  // excluded from builder tab counts and selection UIs.
  isHidden?: boolean;
  excelSheetId?: string;
}

export interface SQLParameter {
  param_name: string;
  param_value: string;
  param_type: "text" | "cell_ref_a1";
}

export type GetOauthAuthorizationUrlProps = {
  email: string | undefined;
  oauthAttemptId: string | undefined;
  dataSourceId: string | undefined;
  dataSourceType: DataSourceType | "slack" | undefined;
  ppress_params?: Record<string, unknown>;
  ppress_metadata?: Record<string, unknown>;
};

// a AugmentedDataImport is a DataImport object *plus* an array of "files"
// and a string that holds the transient status of the import in the UI
// TODO probably not the most elegant solution
export type AugmentedDataImport = DataImport & {
  files?: DataImportFiles; // for CSV
  gdriveDataSource?: DataSource; // for GSheets + CSV GDrive & Upload
  reportId?: string; // for Salesforce
  tableauWorksheetZones?: TableauWorksheetZone[];
  google_analytics_schema?: GoogleAnalyticsSchema;
  icon_background_color?: string;
  icon_name?: string;
  is_custom_api_previous_import?: boolean;
} & DataImportStatus;

enum CoeffGasErrorType {
  DataSourceNeedsReauth = "____DataSourceNeedsReauth____",
}

// Used to annotate certain exceptions thrown from Apps Script functions, so FE JS can take
// actions based on CoeffGasError.Type enum values. Since GAS exceptions are marshalled in
// a somewhat limited manner to FE JS, our hack is to simply prefix Error.message with the .Type
// enum string value.
export const CoeffGasError = {
  Type: CoeffGasErrorType,

  create(type: CoeffGasErrorType, message?: string | undefined): Error {
    return new Error(`${type}:${message || ""}`);
  },

  isErrorOfType(e: Error, type: CoeffGasErrorType): boolean {
    // When GAS marshalls an Apps Script Error object to a FE exception, the "message" property
    // gets "Error: " prepended to it. So, this function allows for this prefix and other
    // simply prefixes. See regex below.
    if (!e.message) {
      return false;
    }
    // Strip prefix (alphanumeric + some allowed punctuation), case-insensitive, ending with colon
    const message = e.message.replace(/^\s*[a-z0-9-_ !.]{1,20}\s*:\s*/i, "");
    return message.indexOf(type) === 0;
  },
};

export interface GetDataImportOptions {
  forRunOnly?: boolean;
}

export interface IDataImporter<TDataImportExtras> {
  newDataImport(options: TDataImportExtras): DataImport & TDataImportExtras;

  augmentDataImport(
    dataImport: DataImport,
    opts: GetDataImportOptions
  ): DataImport & TDataImportExtras;
}

export enum DataImportStatusLevel {
  Error = "error",
  Warning = "warning",
}

const Config = API_KEYS[COEFF_ENV];

export { BuildFlags, Config };

export const CoeffHeaderNumColumns = 0;
export const CoeffHeaderNumRows = 1;
export const CoeffHeaderVersion = 2;

// Deprecate this type and migrate to spec.CoeffFEMetadata
export interface CoeffGasUserProperties {
  coeff__onboarding_views_app?: string[] | null;
  coeff__onboarding_views_alerts?: string[] | null;
  coeff__onboarding_views_writebacks?: string[] | null;
  coeff__onboarding_views_pivot_builder?: string[] | null;
  coeff__onboarding_views_chart_builder?: string[] | null;
  coeff__onboarding_views_sql_builder?: string[] | null;
  coeff__onboarding_eligible?: "1" | null;
  coeff__onboarding_alerts_tryout?: string | null;
  coeff__seen_getstarted_video?: boolean | null;
  coeff__seen_dashboard_builder_onboarding?: boolean | null;
  coeff__seen_smart_alerts_onboarding?: boolean | null;
  coeff__onboarding_beacon_alert_cta?: boolean | null;
  coeff__onboarding_beacon_filters?: boolean | null;
  coeff__onboarding_beacon_pivots?: boolean | null;
  coeff__onboarding_beacon_pivot_builder?: boolean | null;
  coeff__onboarding_beacon_pivot_builder_cta?: boolean | null;
  coeff__onboarding_beacon_chart_builder?: boolean | null;
  coeff__onboarding_beacon_sql_builder?: boolean | null;
  coeff__import_options_beacon_sql_builder?: boolean | null;
  coeff__import_preview_beacon_sql_builder?: boolean | null;
  coeff__free_trial_expired_shown?: boolean | null;
  coeff__user_id?: string | null;
  coeff__last_clock_trigger_reset_ts?: string | null;
  coeff__created_sql_parameter?: boolean | null;
  coeff__install_attribution_iframe_shown_ts?: number | null;
  coeff__signup_attribution_v2_claimed_ts?: number | null;
  coeff__data_import_view_recipes_badge_clicked?: boolean | null;
  coeff__data_import_create_recipe_badge_clicked?: boolean | null;
  coeff__data_import_recipe_import_message_dismissed?: boolean | null;
  coeff__joined_formula_builder_waitlist?: boolean | null;
  coeff__joined_pivot_builder_waitlist?: boolean | null;
  coeff__joined_chart_builder_waitlist?: boolean | null;
  coeff__gpt_features_disabled?: boolean | null;
  coeff__writeback_preview_clicked?: boolean | null;
  coeff__sql_schema_pane_collapsed?: boolean | null;
}

export interface CoeffGasSheetProperties {
  coeff_tpl_info?: string | null;
}

export interface CoeffTemplateInfo {
  is_coeff_tpl?: boolean;
  is_coeff_contest?: boolean;
  coeff_tpl_name?: string | null;
  coeff_tpl_data_import_ids?: string[];
  tpl_setup_completed?: boolean;
}

export type AuthTokens = {
  openIdToken: string;
  oauthToken: string;
  tempUserKey: string;
};

export const TEMPLATE_ICON_SELECTOR_ICON_COLOR = {
  cyan: "#00B3FF",
  blue: "#3176FF",
  purple: "#9D3BFF",
  pink: "#FF56EE",
  aqua: "#00C8C8",
  green: "#00B860",
  red: "#FB565E",
  orange: "#FF8400",
  yellow: "#FFA800",
  lime: "#8C8F93",
};

export const TEMPLATE_ICON_SELECTOR_ICON_NAME = {
  bank: "Bank",
  barchart: "BarChart",
  bell: "Bell",
  book: "Book",
  buoy: "Buoy",
  calculator: "Calculator",
  calendar: "Calendar",
  chat: "Chat",
  click: "Click",
  clock: "Clock",
  currency: "Currency",
  diamond: "Diamond",
  document: "Document",
  email: "Email",
  gift: "Gift",
  glasses: "Glasses",
  gold: "Gold",
  heart: "Heart",
  journey: "Journey",
  lab: "Lab",
  lightening: "Lightening",
  lock: "Lock",
  market: "Market",
  piechart: "PieChart",
  rocket: "Rocket",
  speaker: "Speaker",
  star: "Star",
  ufo: "Ufo",
  user: "User",
  web: "Web",
};

export type TabIdentifier = {
  gsheetsTabId?: number;
  excelSheetId?: string;
};

export type ExcelPlatformAttributes = {
  excel_platform_type: string;
  excel_file_location_type?: ExcelFileLocationType;
  excel_desktop_version?: string;
};

export type PlatformAttributes = ExcelPlatformAttributes;

export type RangeSuggestion = {
  sheetName: string;
  sheetId: number;
  range: string;
};

export type CustomFunctionParams = {
  mode: "all_tabs" | "current_tab" | "selected_cells";
  dataSourceType: DataSourceType | undefined;
  selectedCellA1s?: Array<string>;
};

export type CustomFunctionA1sBySheetId = Record<string, string[]>;

export type ChartImage = {
  chartId: number;
  // null when Google Apps Script's getAs("image/png") fails to render the
  // chart preview — see https://issuetracker.google.com/issues/182701055.
  // The chart is still selectable; the picker UI shows a placeholder.
  img: string | null;
  sheetName: string;
  sheetId: number;
  chartType: string;
  title: string;
  containerInfo: {
    anchorRow: number;
    anchorColumn: number;
  };
};

export type PivotTable = {
  tabName: string;
  sheetId: number;
  anchorCellA1: string;
};
